import type { RouteLocationNormalizedGeneric } from '#vue-router'

import { AppRoute } from '@ts-routes/enums'

import { useListsStore } from '@stores/lists'
import { useLessonsStore } from '@stores/lessons'

import { MAX_PER_PAGE, toIdValue, toPaginationValue } from '@helpers/route'
import { resolveUrl } from '@helpers/resolve-url'
import { foldBracketArrayParams } from '@helpers/query-params'
import {
  LESSONS_ARRAY_QUERY_KEYS,
  getCanonicalLessonsQuery,
  queryToLessonsFilters,
} from '@helpers/lessons-query'

import { defineNuxtRouteMiddleware, navigateTo } from '#app'

/**
 * `/disciplines/{id}/lessons` — query → store filters, as `middleware/discipline-tasks.ts` does. A query the
 * filters cannot show (app-main's `status[0]=…`, a lone date bound, `sort_direction=DESC`) is rewritten once, so
 * the URL, the controls and the request always agree. The legacy `?booking_id=` link, which opened the lesson's
 * edit modal, now opens the lesson page.
 */
export default defineNuxtRouteMiddleware(async (to: RouteLocationNormalizedGeneric) => {
  const bookingId = toIdValue(to.query.booking_id)
  const lessonUrl = bookingId ? resolveUrl(AppRoute.LessonsEdit, { id: to.params.id as string, lessonId: bookingId }) : null

  if (lessonUrl) return navigateTo(lessonUrl, { replace: true })

  const foldedQuery = foldBracketArrayParams(to.query, LESSONS_ARRAY_QUERY_KEYS)
  const query = foldedQuery ?? to.query

  const lists = await useListsStore().ensure()
  const filters = queryToLessonsFilters(query, lists?.booking ?? null)
  const canonicalQuery = getCanonicalLessonsQuery(query, filters)

  if (foldedQuery || canonicalQuery) {
    return navigateTo({ path: to.path, query: canonicalQuery ?? query }, { replace: true })
  }

  const store = useLessonsStore()

  store.setFilters(filters)
  store.setCurrentPage(toPaginationValue(to.query?.page, 1))
  store.setPerPage(toPaginationValue(to.query?.per_page, store.getPerPage, MAX_PER_PAGE))
  store.closeDeleteModal()
  store.closeActionModal()
  // The page reads the list right after (on mount or on the query change): the filters wait for it from the start.
  store.setLoading(true)
})
